import { fetchAuthSession } from '@aws-amplify/auth'
import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source'
import { useEffect } from 'react'
import { useQueryClient } from 'react-query'

import { ISSUE_AGENTS_QUERY_KEY } from 'features/trend-detection/features/agent-list/hooks/use-agents-list'

export type AgentKind = 'benchmarking' | 'issue'
export type AgentRunStatus = 'running' | 'completed' | 'failed'

export interface AgentEvent {
  project_uuid: string
  agent_kind: AgentKind
  agent_uuid: string
  /** Display name for completion toasts; '' for an ad-hoc (agent-less) run. */
  agent_name: string
  run_uuid: string
  status: AgentRunStatus
}

/** SSE frame name emitted by `GET /agent-events`. */
const AGENT_EVENT_NAME = 'agent_run_status_changed'

/**
 * Thrown from `onopen` when the stream is rejected with a non-retriable auth
 * status (401/403). `onerror` rethrows it to stop the reconnect loop instead of
 * retrying forever — an expired/absent session would otherwise reconnect with
 * stale-or-no credentials on every backoff tick, flooding the backend with 401s.
 * A genuinely expired session is redirected to sign-in by the axios `ApiClient`
 * on its next request; a refreshable one self-heals because `fetchAuthSession()`
 * re-mints the token on each (re)connect.
 */
export class FatalAgentStreamError extends Error {}

// One open connection fans out to every interested component. Components that
// need to react to a specific event (e.g. the benchmarking banner toast) call
// `useAgentEventSubscription` instead of opening a second EventSource — the
// browser caps concurrent connections and a per-component stream would also
// re-run the connect-time visibility query on the backend each time.
type AgentEventHandler = (event: AgentEvent) => void
const subscribers = new Set<AgentEventHandler>()

function notifySubscribers(event: AgentEvent): void {
  for (const handler of subscribers) {
    handler(event)
  }
}

function parseAgentEvent(data: string): AgentEvent | null {
  try {
    const parsed = JSON.parse(data) as Partial<AgentEvent>
    if (
      typeof parsed.agent_uuid === 'string' &&
      typeof parsed.run_uuid === 'string' &&
      (parsed.agent_kind === 'benchmarking' || parsed.agent_kind === 'issue')
    ) {
      // Default agent_name so a frame from a not-yet-upgraded backend (no
      // agent_name field) still parses; the toast renders an empty name.
      return { ...parsed, agent_name: parsed.agent_name ?? '' } as AgentEvent
    }
  } catch {
    // ignore malformed frames
  }
  return null
}

/**
 * Opens ONE SSE connection to `GET /agent-events` for the active project and
 * invalidates the agent-status query keys when a run transitions. It also feeds
 * the app-wide completion/failure toasts (see `useAgentRunToasts`), which is why
 * it is mounted once, app-wide, in `ProjectLayout` rather than per page — the
 * stream replaced the old short-interval status poll entirely, so there is no
 * poll fallback. A dropped stream auto-reconnects (`fetchEventSource` backoff),
 * but an auth rejection (401/403) is terminal — see `FatalAgentStreamError` — so
 * an expired/absent session stops instead of reconnecting forever.
 *
 * `@microsoft/fetch-event-source` is used over the native `EventSource` because
 * the latter cannot set the Amplify `Authorization` header; this also gives us
 * auto-reconnect with `Last-Event-ID` for free.
 *
 * The backend snapshots the caller's visible-agent set at connect time, so an
 * agent first made visible to the user *mid-connection* (e.g. a coworker shares
 * one) won't deliver SSE events until the connection re-opens — which happens on
 * any reconnect, or on the next full page load (a fresh `ProjectLayout` mount).
 */
export function useAgentEventsStream(
  projectId: string | null | undefined,
): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!projectId) return

    const controller = new AbortController()
    const base =
      (import.meta.env.VITE_YOGI_API_BASE_URL as string | undefined) ?? ''

    const onAgentEvent = (event: AgentEvent): void => {
      if (event.agent_kind === 'issue') {
        void queryClient.invalidateQueries([ISSUE_AGENTS_QUERY_KEY, projectId])
        void queryClient.invalidateQueries(['trend-feed-status', projectId])
      } else {
        void queryClient.invalidateQueries(['benchmarking-agents', projectId])
        void queryClient.invalidateQueries([
          'benchmarking-insights-feed',
          projectId,
        ])
      }
      notifySubscribers(event)
    }

    void fetchEventSource(
      `${base}/client/agent-events?proj_uuid=${projectId}`,
      {
        signal: controller.signal,
        // Background notification stream — keep the connection alive when the tab
        // is hidden so a run that finishes off-screen still invalidates on focus.
        openWhenHidden: true,
        onmessage(message) {
          if (message.event && message.event !== AGENT_EVENT_NAME) return
          if (!message.data) return
          const event = parseAgentEvent(message.data)
          if (event) onAgentEvent(event)
        },
        async onopen(response) {
          if (
            response.ok &&
            response.headers
              .get('content-type')
              ?.startsWith(EventStreamContentType)
          ) {
            return
          }
          if (response.status === 401 || response.status === 403) {
            // Non-retriable: the caller isn't authorized for this stream. Stop
            // the reconnect loop rather than hammering the backend with a
            // credential-less request on every backoff tick.
            throw new FatalAgentStreamError(
              `agent-events auth ${response.status}`,
            )
          }
          // Any other unexpected response (5xx, wrong content-type) is treated
          // as a transient drop and retried via onerror below.
          throw new Error(`agent-events unexpected response ${response.status}`)
        },
        onerror(err) {
          // Auth rejection is terminal — rethrow so the library stops retrying
          // (an unauthorized session would otherwise reconnect forever).
          if (err instanceof FatalAgentStreamError) throw err
          // Otherwise a transient drop: returning undefined lets the library
          // retry with backoff. There is no poll fallback, so a dropped stream
          // simply reconnects and resumes from Last-Event-ID.
          return undefined
        },
        // Resolve a fresh idToken per (re)connect and attach it as the Amplify
        // Authorization header — native EventSource can't set headers, which is
        // why this library (with a custom fetch) is used.
        fetch: async (input, init) => {
          const { tokens } = await fetchAuthSession()
          const token = tokens?.idToken?.toString()
          const headers = new Headers(init?.headers)
          if (token) headers.set('Authorization', token)
          return fetch(input, { ...init, headers })
        },
      },
    ).catch((err: unknown) => {
      // The library rejects ONLY when onerror rethrows (a clean abort and a
      // normal close both resolve), so the terminal auth failure above is the
      // only error that reaches here. Swallow it — the stream is meant to
      // stop. Without this catch the rejection is unhandled: a console error
      // plus a Sentry capture per affected page load, which would just trade
      // the Datadog 401 flood for a Sentry one.
      if (err instanceof FatalAgentStreamError) return
      // eslint-disable-next-line no-console -- surface an unexpected stream failure; mirrors use-voice-stream's convention
      console.warn('agent-events stream ended unexpectedly', err)
    })

    return () => controller.abort()
  }, [projectId, queryClient])
}

/**
 * Subscribe to forwarded agent events from the single `useAgentEventsStream`
 * connection. The handler is kept in a ref-free closure here because callers
 * pass a stable `useCallback`; re-subscribing on identity change is cheap.
 */
export function useAgentEventSubscription(handler: AgentEventHandler): void {
  useEffect(() => {
    subscribers.add(handler)
    return () => {
      subscribers.delete(handler)
    }
  }, [handler])
}
